import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import CoTrader from "./CoTrader";
import CoTraderStocks from "./CoTraderStocks";
import CoTraderCombinedFilter from "./CoTraderCombinedFilter";
import CoTraderDemoTopBar from "./CoTraderDemoTopBar";
import {
  DEFAULT_FX_TRADE_TYPE,
  EMPTY_TICKER_UNIVERSE,
  FX_ASSET_TYPE,
  FX_TRADE_TYPE_OPTIONS,
  fetchTickerUniverse,
  flattenAssetOptions,
  getFxRuntimeSymbol,
  getAssetTypeLabel,
  resolveTickerUniverseSelection,
} from "./TickerUniverseOptions";

const DEMO_SELECTION_STORAGE_KEY = "cotrader_demo_combined_selection";
const DEMO_FX_TRADE_TYPE_OPTIONS = [FX_TRADE_TYPE_OPTIONS[0]];

const getStoredSelection = () => {
  try {
    const storedSelection = JSON.parse(
      localStorage.getItem(DEMO_SELECTION_STORAGE_KEY) || "null",
    );

    if (!storedSelection) {
      return null;
    }

    return {
      ...storedSelection,
      tradeType: DEFAULT_FX_TRADE_TYPE,
    };
  } catch (error) {
    console.warn("Failed to load CoTrader demo selection", error);
    return null;
  }
};

const CoTraderDemo = () => {
  const [tickerUniverse, setTickerUniverse] = useState(EMPTY_TICKER_UNIVERSE);
  const [isUniverseLoading, setIsUniverseLoading] = useState(true);
  const [universeError, setUniverseError] = useState("");
  const [selection, setSelection] = useState(
    () =>
      getStoredSelection() || {
        assetType: "",
        country: null,
        category: null,
        tradeType: DEFAULT_FX_TRADE_TYPE,
        value: "",
      },
  );

  useEffect(() => {
    let isMounted = true;

    const loadTickerUniverse = async () => {
      setIsUniverseLoading(true);
      setUniverseError("");

      try {
        const nextTickerUniverse = await fetchTickerUniverse({ isDemoMode: true });
        if (!isMounted) return;
        setTickerUniverse(nextTickerUniverse);
      } catch (error) {
        if (!isMounted) return;
        setTickerUniverse(EMPTY_TICKER_UNIVERSE);
        setUniverseError(error?.message || "Ticker universe is unavailable.");
      } finally {
        if (isMounted) {
          setIsUniverseLoading(false);
        }
      }
    };

    void loadTickerUniverse();

    return () => {
      isMounted = false;
    };
  }, []);

  const resolvedSelection = useMemo(
    () =>
      resolveTickerUniverseSelection({
        options: tickerUniverse.options,
        defaultSelection: tickerUniverse.defaultSelection,
        selection,
      }),
    [selection, tickerUniverse.defaultSelection, tickerUniverse.options],
  );
  const isFxAsset = resolvedSelection.assetType === FX_ASSET_TYPE;
  const scopedAssetOptions = useMemo(
    () => flattenAssetOptions(tickerUniverse.options, resolvedSelection.assetType),
    [resolvedSelection.assetType, tickerUniverse.options],
  );
  const scopedStockOptions = useMemo(() => {
    if (isFxAsset || !resolvedSelection.assetType) {
      return {};
    }

    return tickerUniverse.options[resolvedSelection.assetType] || {};
  }, [isFxAsset, resolvedSelection.assetType, tickerUniverse.options]);

  useEffect(() => {
    localStorage.setItem(
      DEMO_SELECTION_STORAGE_KEY,
      JSON.stringify({
        assetType: resolvedSelection.assetType,
        country: resolvedSelection.country,
        category: resolvedSelection.category,
        tradeType: resolvedSelection.tradeType,
        value: resolvedSelection.value,
      }),
    );

    if (isFxAsset) {
      localStorage.setItem("default_symbol", getFxRuntimeSymbol(resolvedSelection.value));
      localStorage.setItem(
        "cotrader_combined_trade_type",
        resolvedSelection.tradeType || DEFAULT_FX_TRADE_TYPE,
      );
      return;
    }

    if (resolvedSelection.country) {
      localStorage.setItem("default_country_stock", resolvedSelection.country);
    }
    if (resolvedSelection.category) {
      localStorage.setItem("default_category_stock", resolvedSelection.category);
    }
    if (resolvedSelection.value) {
      localStorage.setItem("default_symbol_stock", resolvedSelection.value);
    }
  }, [isFxAsset, resolvedSelection]);

  const renderEmbeddedProduct = () => {
    if (!resolvedSelection.assetType || !resolvedSelection.option) {
      return (
        <div className="p-4">
          <div className="rounded-4 border bg-white p-4 text-secondary fw-semibold">
            {universeError || "No eligible CoTrader instruments are available for this user."}
          </div>
        </div>
      );
    }

    if (isFxAsset) {
      return (
        <CoTrader
          availableSymbolsOverride={scopedAssetOptions}
          hideTopChrome
          isDemoMode
          instrumentTypeOverride={resolvedSelection.tradeType}
          selectedSymbolOverride={getFxRuntimeSymbol(resolvedSelection.value)}
        />
      );
    }

    return (
        <CoTraderStocks
          assetTypeLabelOverride={getAssetTypeLabel(resolvedSelection.assetType)}
          defaultSymbolOverride={resolvedSelection.value}
          hideTopChrome
          isDemoMode
          selectedCategoryLabelOverride={resolvedSelection.category || ""}
          selectedCountryLabelOverride={resolvedSelection.country || ""}
        selectedSymbolOverride={resolvedSelection.value}
        selectedTickerLabelOverride={resolvedSelection.option.label || ""}
        stockOptionsOverride={scopedStockOptions}
        stockUniverseErrorOverride={universeError}
        stockUniverseLoadingOverride={isUniverseLoading}
      />
    );
  };

  return (
    <>
      <Helmet>
        <title>CoTrader | EMAlpha</title>
      </Helmet>
      <div
        className="w-100 h-100 d-flex flex-column"
        style={{ background: "#eef3f8", minHeight: 0 }}
      >
        <CoTraderDemoTopBar />
        <div className="px-3 pb-2">
          <div
            className="rounded-4 border px-3 py-3"
            style={{
              background: "#F8FAFC",
              borderColor: "#E5EAF1",
            }}
          >
            <CoTraderCombinedFilter
              universeOptions={tickerUniverse.options}
              selection={resolvedSelection}
              onSelectionChange={setSelection}
              isDisabled={isUniverseLoading || Boolean(universeError)}
              tradeTypeOptions={DEMO_FX_TRADE_TYPE_OPTIONS}
            />
          </div>
        </div>

        <div className="flex-grow-1" style={{ minHeight: 0 }}>
          {renderEmbeddedProduct()}
        </div>
      </div>
    </>
  );
};

export default CoTraderDemo;
